import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import { usePrincipalKey } from "@auth/sessionStore";
import { toPatientSignalment } from "@patients/patientsClient";
import { signalmentOf } from "@patients/signalment";
import { type CallContext, useCallContext } from "@phone/callContextClient";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import { Button } from "@scribenote/brian-design-system";
import { useOpenPatient } from "@shell/navigation";
import { useQuery } from "@tanstack/react-query";
import { useNow } from "@utils/use-now";
import { type FormEvent, type ReactNode, useState } from "react";

type ContextQuery = ReturnType<typeof useCallContext>;
type Patient = components["schemas"]["PatientOut"];
const dateTime = (date: string | null | undefined) =>
  date
    ? new Date(date).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not recorded";
export const SELECTED_CLIENT_NOTE =
  "Viewing selected client · number association not saved";

export const formatMoney = (
  amount: number | null | undefined,
  currency: string,
) =>
  amount == null
    ? "Not calculated"
    : new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
        amount / 100,
      );

const Section = ({
  title,
  count,
  limited,
  children,
}: {
  title: string;
  count: number;
  limited?: boolean;
  children: ReactNode;
}) => (
  <details className="border-t border-rule py-2">
    <summary className="cursor-pointer text-sm font-medium text-ink">
      {title}{" "}
      <span className="text-annotation">
        ({count}
        {limited ? "+" : ""})
      </span>
    </summary>
    <div className="mt-2 space-y-3 text-sm text-ink-body">
      {count ? (
        children
      ) : (
        <p className="text-annotation">No saved records available.</p>
      )}
      {limited && (
        <p className="text-fine-print text-annotation">
          Showing a bounded selection of saved records.
        </p>
      )}
    </div>
  </details>
);

const ClientSearch = ({ context }: { context: ContextQuery }) => {
  const principal = usePrincipalKey();
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const results = useQuery({
    queryKey: ["call-client-search", principal, search],
    queryFn: ({ signal }) =>
      request(() =>
        apiClient.GET("/clients", {
          params: { query: { name: search, limit: 10 } },
          signal,
        }),
      ),
    enabled: search.length >= 2,
    staleTime: 30_000,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const submitted = input.trim();
    if (submitted === search) void results.refetch();
    else setSearch(submitted);
  };
  return (
    <div className="space-y-2 border-t border-rule pt-2">
      <form className="flex gap-2" onSubmit={submit}>
        <input
          aria-label="Search clients by name"
          placeholder="Search clients by name"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          className="min-w-0 flex-1 rounded-sm border border-rule bg-card px-2 py-1 text-sm"
        />
        <Button
          size="sm"
          variant="outline"
          type="submit"
          disabled={input.trim().length < 2}
          busy={results.isFetching}
        >
          Search
        </Button>
      </form>
      {results.isError && (
        <p role="status" className="text-sm text-ink-stopped">
          Couldn't search clients. Try again.
        </p>
      )}
      {results.data?.map((client) => (
        <button
          key={client.uuid}
          type="button"
          onClick={() => context.selectClient(client.uuid)}
          className="block w-full cursor-pointer rounded-sm border border-rule p-2 text-left hover:bg-yellow-wash"
        >
          <span className="font-medium">{client.name}</span>
          <span className="block text-fine-print text-annotation">
            {[client.phone && formatPhoneNumber(client.phone), client.email]
              .filter(Boolean)
              .join(" · ") || "No contact details saved"}
          </span>
        </button>
      ))}
      {results.data?.length === 0 && (
        <p className="text-sm text-annotation">
          No clients found for this name.
        </p>
      )}
      {results.data?.length === 10 && (
        <p className="text-fine-print text-annotation">
          First 10 matches. Refine the name to narrow the results.
        </p>
      )}
      <p className="text-fine-print text-annotation">
        Selection opens saved client information for this call. It does not save
        a number association.
      </p>
    </div>
  );
};

const PatientDetails = ({ patient }: { patient: Patient }) => {
  const principal = usePrincipalKey();
  const [open, setOpen] = useState(false);
  const openPatient = useOpenPatient();
  const measurements = useQuery({
    queryKey: ["call-patient-measurements", principal, patient.uuid],
    queryFn: ({ signal }) =>
      request(() =>
        apiClient.GET("/measurements", {
          params: {
            query: { entity_uuid: patient.uuid, resolved: true, limit: 12 },
          },
          signal,
        }),
      ),
    enabled: open,
    staleTime: 30_000,
  });
  const signalment = signalmentOf(toPatientSignalment(patient));
  return (
    <details
      onToggle={(event) => setOpen(event.currentTarget.open)}
      className="rounded-sm border border-rule p-2"
    >
      <summary className="cursor-pointer font-medium">
        {patient.name}{" "}
        <span className="font-normal text-annotation">
          {[signalment.species, signalment.age].filter(Boolean).join(" · ")}
          {patient.deceased_at ? " · Deceased" : ""}
        </span>
      </summary>
      <div className="mt-2 space-y-1">
        <p>
          {[signalment.breed, signalment.sex, patient.color]
            .filter(Boolean)
            .join(" · ") || "Signalment not recorded"}
        </p>
        {patient.microchip && <p>Microchip: {patient.microchip}</p>}
        {patient.deceased_at && (
          <p>Deceased: {dateTime(patient.deceased_at)}</p>
        )}
        {measurements.isPending && open && (
          <p className="text-annotation">Loading measurements…</p>
        )}
        {measurements.isError && (
          <p className="text-ink-stopped">Couldn't load measurements.</p>
        )}
        {measurements.data?.length === 0 && (
          <p className="text-annotation">No saved measurements.</p>
        )}
        {measurements.data?.map((measurement) => (
          <p key={measurement.uuid} className="text-fine-print">
            {measurement.kind.replace(/_/g, " ")}: {measurement.value}{" "}
            {measurement.unit} · {dateTime(measurement.observed_at)}
          </p>
        ))}
        {measurements.data?.length === 12 && (
          <p className="text-fine-print text-annotation">
            Showing the latest 12 measurement records.
          </p>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => openPatient(patient.uuid)}
        >
          Open patient record
        </Button>
      </div>
    </details>
  );
};

const ContextSections = ({
  data,
  context,
}: {
  data: CallContext;
  context: ContextQuery;
}) => {
  const limited = (section: string) => data.truncated.includes(section);
  return (
    <>
      {(data.match_status !== "unique" || context.selectedClientUuid) && (
        <>
          {data.candidates.length > 0 && (
            <Section
              title="Matching clients"
              count={data.candidates.length}
              limited={limited("candidates")}
            >
              {data.candidates.map((client) => (
                <Button
                  key={client.uuid}
                  size="sm"
                  variant="outline"
                  onClick={() => context.selectClient(client.uuid)}
                >
                  {client.name}
                </Button>
              ))}
            </Section>
          )}
          <ClientSearch context={context} />
        </>
      )}
      {data.client && (
        <>
          <Section title="Contact" count={1} limited={limited("phone_numbers")}>
            <p>{data.client.name}</p>
            <p>{data.client.email ?? "Email not recorded"}</p>
            {data.phone_numbers.map((number) => (
              <p key={number.uuid}>
                {formatPhoneNumber(number.number_e164)} · {number.label}
                {number.is_primary ? " · Primary" : ""}
              </p>
            ))}
            {data.client.phone && (
              <p>Saved contact: {formatPhoneNumber(data.client.phone)}</p>
            )}
            <p className="text-fine-print text-annotation">
              Client since {dateTime(data.client.created_at)} · Updated{" "}
              {dateTime(data.client.updated_at)}
            </p>
          </Section>
          <Section
            title="Patients"
            count={data.patients.length}
            limited={limited("patients")}
          >
            {data.patients.map((patient) => (
              <PatientDetails key={patient.uuid} patient={patient} />
            ))}
          </Section>
          <Section
            title="Visits and appointments"
            count={data.visits.length}
            limited={limited("visits")}
          >
            {data.visits.map((visit) => (
              <div key={visit.uuid}>
                <p className="font-medium">
                  {visit.patient_name} · {visit.visit_type}
                  {visit.cancelled_at ? " · Cancelled" : ""}
                </p>
                <p>{dateTime(visit.scheduled_start ?? visit.created_at)}</p>
                {visit.reason && <p>{visit.reason}</p>}
                <p className="text-fine-print text-annotation">
                  {[visit.assigned_user_name, visit.current_stage?.name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            ))}
          </Section>
          <Section
            title="Visible tasks"
            count={data.tasks.length}
            limited={limited("tasks")}
          >
            {data.tasks.map((task) => (
              <div key={task.uuid}>
                <p className="font-medium">
                  {task.title} · {task.status}
                  {task.severity === "urgent" ? " · Urgent" : ""}
                </p>
                {task.details && (
                  <p className="whitespace-pre-wrap">{task.details}</p>
                )}
                {task.due_at && <p>Due {dateTime(task.due_at)}</p>}
              </div>
            ))}
          </Section>
          <Section
            title="Saved notes"
            count={data.memos.length}
            limited={limited("memos")}
          >
            {data.memos.map((memo) => (
              <div key={memo.uuid}>
                <p className="text-fine-print text-annotation">
                  {memo.record_class} · {dateTime(memo.created_at)}
                </p>
                <p className="whitespace-pre-wrap">{memo.body}</p>
              </div>
            ))}
          </Section>
          <Section
            title="Invoices and payments"
            count={data.invoices.length}
            limited={limited("invoices")}
          >
            {data.invoices.map((invoice) => (
              <details key={invoice.uuid}>
                <summary className="cursor-pointer">
                  {invoice.kind.replace(/_/g, " ")} · {invoice.status} ·{" "}
                  {invoice.settlement_status} ·{" "}
                  {formatMoney(invoice.total_amount, invoice.currency)}
                </summary>
                <div className="mt-1 space-y-1">
                  <p>Issued {dateTime(invoice.issued_at)}</p>
                  <p>
                    Paid {formatMoney(invoice.paid_amount, invoice.currency)} ·
                    Balance{" "}
                    {formatMoney(invoice.balance_amount, invoice.currency)}
                  </p>
                  {invoice.lines.map((line) => (
                    <p key={line.uuid}>
                      {line.description} · {line.quantity} {line.unit ?? ""} ·{" "}
                      {formatMoney(line.charged_amount, invoice.currency)}
                      {line.status !== "active" ? ` · ${line.status}` : ""}
                    </p>
                  ))}
                  {invoice.allocations.map((allocation) => (
                    <p key={allocation.uuid}>
                      Payment{" "}
                      {formatMoney(allocation.amount, allocation.currency)} ·{" "}
                      {dateTime(allocation.received_at)}
                    </p>
                  ))}
                </div>
              </details>
            ))}
          </Section>
        </>
      )}
      <Section
        title={
          data.client ? "Prior calls and messages" : "Calls from this number"
        }
        count={data.prior_calls.length}
        limited={limited("prior_calls")}
      >
        {data.prior_calls.map((call) => (
          <div key={call.uuid}>
            <p className="font-medium">
              {dateTime(call.created_at)} ·{" "}
              {call.origin === "inbound" ? "Incoming" : "Outgoing"}
            </p>
            <p className="text-fine-print text-annotation">
              {call.disposition?.replace(/_/g, " ") ??
                call.status.replace(/_/g, " ")}
            </p>
            {call.message && (
              <p className="whitespace-pre-wrap">Message: {call.message}</p>
            )}
            {call.summary && (
              <p className="whitespace-pre-wrap">{call.summary}</p>
            )}
          </div>
        ))}
      </Section>
    </>
  );
};

export const CallContextPanel = ({ context }: { context: ContextQuery }) => {
  const now = useNow();
  const data = context.data;
  const nextVisit = data?.visits
    .filter(
      (visit) =>
        !visit.cancelled_at &&
        visit.scheduled_start &&
        new Date(visit.scheduled_start).getTime() >= now,
    )
    .sort((a, b) => a.scheduled_start!.localeCompare(b.scheduled_start!))[0];
  const openTasks = data?.tasks.filter((task) => task.status === "open") ?? [];
  return (
    <div
      data-dd-privacy="hidden"
      className="mt-2 min-h-0 text-ink-body"
      data-testid="call-context"
    >
      {context.isPending ? (
        <p className="text-fine-print text-annotation">
          Loading saved caller information…
        </p>
      ) : context.isError ? (
        <div role="status" className="flex items-center justify-between gap-2">
          <p className="text-fine-print text-ink-stopped">
            Saved information unavailable. Call controls still work.
          </p>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void context.refetch()}
          >
            Retry
          </Button>
          {context.selectedClientUuid && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => context.selectClient(null)}
            >
              Clear selection
            </Button>
          )}
        </div>
      ) : (
        data && (
          <>
            <p className="text-fine-print text-annotation">
              {data.match_source === "selection"
                ? SELECTED_CLIENT_NOTE
                : data.match_status === "unique"
                  ? "Linked by saved phone number"
                  : data.match_status === "ambiguous"
                    ? "This number matches multiple clients. Confirm the caller."
                    : "No unique client association for this number."}
            </p>
            {data.provider_name && data.match_status !== "unique" && (
              <p className="text-fine-print text-annotation">
                Provider caller name: {data.provider_name} · Unverified
              </p>
            )}
            {data.client && (
              <p className="mt-1 text-sm">
                {data.patients.map((patient) => patient.name).join(", ") ||
                  "No patients saved"}
              </p>
            )}
            {nextVisit && (
              <p className="mt-1 text-fine-print">
                Next: {nextVisit.patient_name} · {nextVisit.visit_type} ·{" "}
                {dateTime(nextVisit.scheduled_start)}
              </p>
            )}
            {openTasks.length > 0 && (
              <p className="mt-1 text-fine-print">
                {openTasks.length}
                {data.truncated.includes("tasks") ? "+" : ""} visible open tasks
                {openTasks.some((task) => task.severity === "urgent")
                  ? " · Urgent task"
                  : ""}
              </p>
            )}
            <div className="mt-1 flex items-center justify-between gap-2">
              <Button
                size="sm"
                variant="ghost"
                aria-expanded={context.expanded}
                onClick={() => context.setExpanded(!context.expanded)}
              >
                {context.expanded ? "Less information" : "More information"}
              </Button>
              {context.selectedClientUuid && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => context.selectClient(null)}
                >
                  Clear selection
                </Button>
              )}
              {context.isFetching && (
                <span className="text-fine-print text-annotation">
                  Updating…
                </span>
              )}
            </div>
            {context.expanded && (
              <div
                className="mt-1 max-h-[min(45dvh,28rem)] overflow-y-auto overscroll-contain break-words pr-1"
                data-testid="call-context-sections"
              >
                <ContextSections data={data} context={context} />
              </div>
            )}
          </>
        )
      )}
    </div>
  );
};
